import { FormEvent, useEffect, useMemo, useState } from "react";
import type { PackageBillingDimension, PepLeadSettings, PepPricingSettings } from "@itaas/pep-contracts";
import { fetchPepSettings, savePepSettings, sendTestEmail } from "./admin-api";

function sortKeys<T>(record: Record<string, T>) {
  return Object.keys(record).sort((a, b) => a.localeCompare(b));
}

function parsePricingText(pricingText: string): PepPricingSettings {
  return JSON.parse(pricingText) as PepPricingSettings;
}

export function AdminSettingsPage() {
  const [settings, setSettings] = useState<PepLeadSettings | null>(null);
  const [recipientsText, setRecipientsText] = useState("");
  const [pricingText, setPricingText] = useState("");
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchPepSettings()
      .then((s) => {
        setSettings(s);
        setRecipientsText(s.notifications.email.recipients.join("\n"));
        setPricingText(JSON.stringify(s.pricing, null, 2));
      })
      .catch(() => setError("Impossible de charger les paramètres."));
  }, []);

  const pricing = useMemo(() => {
    try {
      return parsePricingText(pricingText);
    } catch {
      return null;
    }
  }, [pricingText]);

  function updatePricing(mutator: (current: PepPricingSettings) => PepPricingSettings) {
    if (!pricing) return;
    const next = mutator(pricing);
    setPricingText(JSON.stringify(next, null, 2));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!settings) return;
    setError(null);
    setSaved(false);

    const recipients = recipientsText
      .split(/[\n,;]+/)
      .map((r) => r.trim())
      .filter((r) => r.includes("@"));

    try {
      const pricing = JSON.parse(pricingText) as PepLeadSettings["pricing"];
      const next = await savePepSettings({
        notifications: {
          email: {
            enabled: settings.notifications.email.enabled,
            recipients,
          },
          webhook: settings.notifications.webhook,
          ticket: settings.notifications.ticket,
        },
        pricing,
      });
      setSettings(next);
      setRecipientsText(next.notifications.email.recipients.join("\n"));
      setPricingText(JSON.stringify(next.pricing, null, 2));
      setSaved(true);
    } catch {
      setError("Échec de l'enregistrement. Vérifiez le JSON du plan tarifaire.");
    }
  }

  async function onTestEmail() {
    setTesting(true);
    setTestResult(null);
    setError(null);
    try {
      const result = await sendTestEmail();
      if (result.mode === "log_only") {
        setTestResult(
          "Mode LOG_ONLY — SMTP_HOST non configuré. Consultez les logs API.",
        );
      } else if (result.sent) {
        setTestResult(`Email de test envoyé à : ${result.recipients.join(", ")}`);
      } else {
        setTestResult("Aucun destinataire configuré.");
      }
    } catch {
      setError("Échec de l'envoi du test SMTP. Vérifiez SMTP_USER / SMTP_PASS.");
    } finally {
      setTesting(false);
    }
  }

  if (!settings) {
    return <p className="text-secondary">{error ?? "Chargement…"}</p>;
  }

  const productKeys = pricing ? sortKeys(pricing.products) : [];
  const packageKeys = pricing ? sortKeys(pricing.packageRules) : [];

  return (
    <>
      <h1 className="h4 fw-bold mb-2">Paramètres commerciaux</h1>
      <p className="text-secondary small mb-4">
        Aujourd&apos;hui : notification par email. Demain : webhook ou ticket CRM (champs préparés).
      </p>

      <form onSubmit={onSubmit} className="card border-0 shadow-sm p-4 mb-4">
        <h2 className="h6 fw-bold">Notifications email</h2>
        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="emailEnabled"
            checked={settings.notifications.email.enabled}
            onChange={(e) =>
              setSettings({
                ...settings,
                notifications: {
                  ...settings.notifications,
                  email: { ...settings.notifications.email, enabled: e.target.checked },
                },
              })
            }
          />
          <label className="form-check-label" htmlFor="emailEnabled">
            Envoyer un email à chaque nouvelle demande
          </label>
        </div>
        <label className="form-label">Destinataires (un par ligne)</label>
        <textarea
          className="form-control mb-3"
          rows={4}
          value={recipientsText}
          onChange={(e) => setRecipientsText(e.target.value)}
          placeholder="support@itaas-rd.com"
        />
        {error && <div className="alert alert-danger py-2">{error}</div>}
        {saved && <div className="alert alert-success py-2">Paramètres enregistrés.</div>}
        <div className="d-flex flex-wrap gap-2">
          <button type="submit" className="btn btn-primary">
            Enregistrer
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => void onTestEmail()}
            disabled={testing}
          >
            {testing ? "Envoi…" : "Envoyer un email de test"}
          </button>
        </div>
        {testResult && <div className="alert alert-info py-2 mt-3 mb-0">{testResult}</div>}
      </form>

      <form onSubmit={onSubmit} className="card border-0 shadow-sm p-4 mb-4">
        <h2 className="h6 fw-bold">Plan de tarification paramétrable</h2>
        <p className="small text-secondary">
          Modifiez ici les prix, unités, métriques par package et coefficients de réduction bundle.
          Le formulaire met à jour le JSON automatiquement.
        </p>

        {!pricing ? (
          <div className="alert alert-danger py-2">
            Le JSON du plan tarifaire est invalide. Corrigez-le dans l&apos;éditeur brut ci-dessous.
          </div>
        ) : (
          <>
            <div className="card border bg-body-tertiary p-3 mb-4">
              <h3 className="h6 fw-bold mb-3">Devises & remise RDC</h3>
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">USD</label>
                  <input
                    className="form-control"
                    type="number"
                    step="0.01"
                    value={pricing.USD}
                    onChange={(e) =>
                      updatePricing((current) => ({
                        ...current,
                        USD: Number(e.target.value) || 0,
                      }))
                    }
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">CDF</label>
                  <input
                    className="form-control"
                    type="number"
                    step="1"
                    value={pricing.CDF}
                    onChange={(e) =>
                      updatePricing((current) => ({
                        ...current,
                        CDF: Number(e.target.value) || 0,
                      }))
                    }
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">EUR</label>
                  <input
                    className="form-control"
                    type="number"
                    step="0.01"
                    value={pricing.EUR}
                    onChange={(e) =>
                      updatePricing((current) => ({
                        ...current,
                        EUR: Number(e.target.value) || 0,
                      }))
                    }
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Remise RDC (%)</label>
                  <input
                    className="form-control"
                    type="number"
                    step="1"
                    value={pricing.rdcDiscountPercent}
                    onChange={(e) =>
                      updatePricing((current) => ({
                        ...current,
                        rdcDiscountPercent: Number(e.target.value) || 0,
                      }))
                    }
                  />
                </div>
              </div>
            </div>

            <div className="card border bg-body-tertiary p-3 mb-4">
              <h3 className="h6 fw-bold mb-3">Prix par module</h3>
              <div className="d-flex flex-column gap-3">
                {productKeys.map((productKey) => {
                  const product = pricing.products[productKey];
                  return (
                    <div key={productKey} className="border rounded p-3 bg-white">
                      <div className="fw-semibold mb-2">{productKey}</div>
                      <div className="row g-3">
                        <div className="col-md-3">
                          <label className="form-label">Starter</label>
                          <input
                            className="form-control"
                            type="number"
                            step="0.01"
                            value={product.starter ?? ""}
                            onChange={(e) =>
                              updatePricing((current) => ({
                                ...current,
                                products: {
                                  ...current.products,
                                  [productKey]: {
                                    ...current.products[productKey],
                                    starter: e.target.value === "" ? null : Number(e.target.value),
                                  },
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Professional</label>
                          <input
                            className="form-control"
                            type="number"
                            step="0.01"
                            value={product.professional ?? ""}
                            onChange={(e) =>
                              updatePricing((current) => ({
                                ...current,
                                products: {
                                  ...current.products,
                                  [productKey]: {
                                    ...current.products[productKey],
                                    professional:
                                      e.target.value === "" ? null : Number(e.target.value),
                                  },
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Enterprise</label>
                          <input
                            className="form-control"
                            type="number"
                            step="0.01"
                            value={product.enterprise ?? ""}
                            onChange={(e) =>
                              updatePricing((current) => ({
                                ...current,
                                products: {
                                  ...current.products,
                                  [productKey]: {
                                    ...current.products[productKey],
                                    enterprise:
                                      e.target.value === "" ? null : Number(e.target.value),
                                  },
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Unité</label>
                          <input
                            className="form-control"
                            value={product.unit ?? ""}
                            onChange={(e) =>
                              updatePricing((current) => ({
                                ...current,
                                products: {
                                  ...current.products,
                                  [productKey]: {
                                    ...current.products[productKey],
                                    unit: e.target.value,
                                  },
                                },
                              }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="card border bg-body-tertiary p-3 mb-4">
              <h3 className="h6 fw-bold mb-3">Règles bundle par package</h3>
              <div className="accordion" id="pricing-rules-accordion">
                {packageKeys.map((packageKey, packageIndex) => {
                  const rule = pricing.packageRules[packageKey];
                  return (
                    <div className="accordion-item" key={packageKey}>
                      <h4 className="accordion-header">
                        <button
                          className={`accordion-button ${packageIndex === 0 ? "" : "collapsed"}`}
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target={`#pricing-rule-${packageKey}`}
                        >
                          {packageKey}
                        </button>
                      </h4>
                      <div
                        id={`pricing-rule-${packageKey}`}
                        className={`accordion-collapse collapse ${packageIndex === 0 ? "show" : ""}`}
                        data-bs-parent="#pricing-rules-accordion"
                      >
                        <div className="accordion-body">
                          <div className="mb-4">
                            <div className="fw-semibold mb-2">Coefficients bundle</div>
                            <div className="row g-3">
                              {sortKeys(rule.moduleDiscounts ?? {}).map((moduleSlug) => (
                                <div className="col-md-4" key={moduleSlug}>
                                  <label className="form-label">{moduleSlug}</label>
                                  <div className="input-group">
                                    <input
                                      className="form-control"
                                      type="number"
                                      step="0.05"
                                      min={0}
                                      max={1}
                                      value={rule.moduleDiscounts?.[moduleSlug] ?? 1}
                                      onChange={(e) =>
                                        updatePricing((current) => ({
                                          ...current,
                                          packageRules: {
                                            ...current.packageRules,
                                            [packageKey]: {
                                              ...current.packageRules[packageKey],
                                              moduleDiscounts: {
                                                ...current.packageRules[packageKey].moduleDiscounts,
                                                [moduleSlug]: Number(e.target.value) || 0,
                                              },
                                            },
                                          },
                                        }))
                                      }
                                    />
                                    <span className="input-group-text">x</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div>
                            <div className="fw-semibold mb-2">Métriques</div>
                            <div className="d-flex flex-column gap-3">
                              {rule.billingDimensions.map((dim, dimIndex) => (
                                <div className="border rounded p-3" key={`${packageKey}-${dim.key}`}>
                                  <div className="row g-3">
                                    <div className="col-md-4">
                                      <label className="form-label">Libellé FR</label>
                                      <input
                                        className="form-control"
                                        value={dim.labelFr}
                                        onChange={(e) =>
                                          updatePricing((current) => {
                                            const dims = [...current.packageRules[packageKey].billingDimensions];
                                            dims[dimIndex] = { ...dims[dimIndex], labelFr: e.target.value };
                                            return {
                                              ...current,
                                              packageRules: {
                                                ...current.packageRules,
                                                [packageKey]: {
                                                  ...current.packageRules[packageKey],
                                                  billingDimensions: dims,
                                                },
                                              },
                                            };
                                          })
                                        }
                                      />
                                    </div>
                                    <div className="col-md-4">
                                      <label className="form-label">Clé</label>
                                      <input
                                        className="form-control"
                                        value={dim.key}
                                        onChange={(e) =>
                                          updatePricing((current) => {
                                            const dims = [...current.packageRules[packageKey].billingDimensions];
                                            dims[dimIndex] = { ...dims[dimIndex], key: e.target.value };
                                            return {
                                              ...current,
                                              packageRules: {
                                                ...current.packageRules,
                                                [packageKey]: {
                                                  ...current.packageRules[packageKey],
                                                  billingDimensions: dims,
                                                },
                                              },
                                            };
                                          })
                                        }
                                      />
                                    </div>
                                    <div className="col-md-2">
                                      <label className="form-label">Défaut</label>
                                      <input
                                        className="form-control"
                                        type="number"
                                        value={dim.defaultQty}
                                        onChange={(e) =>
                                          updatePricing((current) => {
                                            const dims = [...current.packageRules[packageKey].billingDimensions];
                                            dims[dimIndex] = {
                                              ...dims[dimIndex],
                                              defaultQty: Number(e.target.value) || 0,
                                            };
                                            return {
                                              ...current,
                                              packageRules: {
                                                ...current.packageRules,
                                                [packageKey]: {
                                                  ...current.packageRules[packageKey],
                                                  billingDimensions: dims,
                                                },
                                              },
                                            };
                                          })
                                        }
                                      />
                                    </div>
                                    <div className="col-md-2">
                                      <label className="form-label">Minimum</label>
                                      <input
                                        className="form-control"
                                        type="number"
                                        value={dim.minQty ?? 1}
                                        onChange={(e) =>
                                          updatePricing((current) => {
                                            const dims = [...current.packageRules[packageKey].billingDimensions];
                                            dims[dimIndex] = {
                                              ...dims[dimIndex],
                                              minQty: Number(e.target.value) || 1,
                                            };
                                            return {
                                              ...current,
                                              packageRules: {
                                                ...current.packageRules,
                                                [packageKey]: {
                                                  ...current.packageRules[packageKey],
                                                  billingDimensions: dims,
                                                },
                                              },
                                            };
                                          })
                                        }
                                      />
                                    </div>
                                  </div>
                                  <div className="row g-3 mt-1">
                                    <div className="col-md-8">
                                      <label className="form-label">Modules liés</label>
                                      <input
                                        className="form-control"
                                        value={dim.moduleSlugs.join(", ")}
                                        onChange={(e) =>
                                          updatePricing((current) => {
                                            const dims = [...current.packageRules[packageKey].billingDimensions];
                                            dims[dimIndex] = {
                                              ...dims[dimIndex],
                                              moduleSlugs: e.target.value
                                                .split(",")
                                                .map((item) => item.trim())
                                                .filter(Boolean),
                                            } as PackageBillingDimension;
                                            return {
                                              ...current,
                                              packageRules: {
                                                ...current.packageRules,
                                                [packageKey]: {
                                                  ...current.packageRules[packageKey],
                                                  billingDimensions: dims,
                                                },
                                              },
                                            };
                                          })
                                        }
                                      />
                                    </div>
                                    <div className="col-md-4">
                                      <label className="form-label">Seuil confirmation</label>
                                      <input
                                        className="form-control"
                                        type="number"
                                        value={dim.confirmBelowQty ?? ""}
                                        onChange={(e) =>
                                          updatePricing((current) => {
                                            const dims = [...current.packageRules[packageKey].billingDimensions];
                                            dims[dimIndex] = {
                                              ...dims[dimIndex],
                                              confirmBelowQty:
                                                e.target.value === "" ? undefined : Number(e.target.value),
                                            };
                                            return {
                                              ...current,
                                              packageRules: {
                                                ...current.packageRules,
                                                [packageKey]: {
                                                  ...current.packageRules[packageKey],
                                                  billingDimensions: dims,
                                                },
                                              },
                                            };
                                          })
                                        }
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        <label className="form-label">JSON du plan tarifaire</label>
        <textarea
          className="form-control font-monospace"
          rows={24}
          value={pricingText}
          onChange={(e) => setPricingText(e.target.value)}
        />
        <div className="small text-secondary mt-2">
          Sections attendues : `USD`, `CDF`, `EUR`, `rdcDiscountPercent`, `products`,
          `packageRules`.
        </div>
        {error && <div className="alert alert-danger py-2 mt-3">{error}</div>}
        {saved && <div className="alert alert-success py-2 mt-3">Paramètres enregistrés.</div>}
        <div className="d-flex flex-wrap gap-2 mt-3">
          <button type="submit" className="btn btn-primary">
            Enregistrer le plan
          </button>
        </div>
      </form>

      <div className="card border-0 shadow-sm p-4 mb-4">
        <h2 className="h6 fw-bold mb-2">Programme ambassadeur</h2>
        <p className="small text-secondary mb-3">
          Commissions, réductions établissements et vérification téléphone sont configurables dans
          la section dédiée.
        </p>
        <a className="btn btn-outline-primary btn-sm" href="/admin/affiliation">
          Paramètres affiliation →
        </a>
      </div>

      <div className="card border-0 shadow-sm p-4 opacity-75">
        <h2 className="h6 fw-bold text-secondary">Bientôt disponible</h2>
        <ul className="small text-secondary mb-0">
          <li>Création automatique de ticket CRM</li>
          <li>Webhook vers outil externe</li>
          <li>Intégration Suite IAM — accès via la Suite (menu Plateforme)</li>
        </ul>
      </div>
    </>
  );
}
